import moment from 'moment';
import { Subscription } from 'rxjs';
import { PassportScanInfoInt } from '@interfaces/*';
import { Passenger as PassengerOld } from '@app/models';
import { FlightSummary, Passenger } from '@app/models/micro';
import {
  CARRY_ON_BASIC_FARE_FAMILIES,
  DocumentationWindowPhase,
  FARES_FAMILY,
  PAX_TYPE,
  StatusBag,
} from '@app/enums';
import { environment } from '@env/environment';
import { getClosingMinutes } from '@utils/functions/general';
import { DOCUMENTATION_WINDOW } from '@utils/constants/generals';
import { validatePnrInput } from '@utils/validations/pnr.validation';

const specialCountryCodeMapping = {
  D: 'DE',
};
function parseDate(day: number, month: number, year: number): string {
  const pDate = moment(`${day}-${month}-${year}`, 'DD-MM-YYYY');

  const sDate = pDate.toISOString(false).split('T');

  return sDate[0];
}

function abbreviateString(string: string, maxLength: number) {
  if (string == null || maxLength == null) {
    return string;
  }
  let newString = string;
  if (newString.length >= maxLength) {
    newString = newString.substring(0, maxLength - 2);
    newString += '..';
  }
  return newString;
}

/**
 * shortens a Frequent Flyer name if the name + number length is >= 32
 * @param passenger
 * @param shortenNames are there enough passengers to cause the name ot shorten
 */
function shortenFfName(passenger, shortenNames = true) {
  if (passenger.frequentFlyerProgram == null || passenger.frequentFlyerNumber == null) {
    return '';
  }
  const characters = passenger.frequentFlyerProgram.length + passenger.frequentFlyerNumber.length;
  const shortenAmount = characters - 32;
  if (shortenNames && shortenAmount > 0) {
    const displayName = passenger.frequentFlyerProgram.substring(
      0,
      passenger.frequentFlyerProgram.length - shortenAmount
    );
    return `${displayName}..`;
  } else return passenger.frequentFlyerProgram;
}

function getUniqueId(parts: number): string {
  const stringArr = [];
  for (let i = 0; i < parts; i++) {
    // tslint:disable-next-line:no-bitwise
    const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    stringArr.push(S4);
  }
  return stringArr.join('-');
}

function validatePNR(pnr) {
  return validatePnrInput(pnr);
}

function unsubscribe(subs: Subscription[]) {
  subs.map((sub) => {
    if (sub) {
      sub.unsubscribe();
    }
  });
}

function parsePassportInfo(track1, track2, track3: string, track4: string) {
  const passportInfo: PassportScanInfoInt = {};

  if (!track1 || !track2) {
    return passportInfo;
  } else if (track1.length < 5 || track2.length < 27) {
    return passportInfo;
  }

  let regExp = new RegExp('<', 'g');
  if (track4) {
    const [image, ...empty] = track4?.split(',').reverse();
    passportInfo.image = image.trim();
  }
  try {
    passportInfo.country = track1.substr(2, 3).replace(regExp, '');

    let name = track1.substr(5);
    if (name.indexOf('<<') > 0) {
      passportInfo.lastName = name.split('<<')[0].replace(regExp, ' ');
      passportInfo.firstName = name.split('<<')[1].replace(regExp, ' ');
    }

    passportInfo.number = track2.substring(0, 9).replace(regExp, '');

    passportInfo.issuingCountry = track2.substring(10, 13).replace(regExp, '');
    passportInfo.issuingCountry = specialCountryCodeMap(passportInfo.issuingCountry);

    let birthdate = track2.substring(13, 19);
    passportInfo.birthDateMonth = birthdate.substr(2, 2);
    passportInfo.birthDateDay = birthdate.substr(4, 2);

    let now = new Date();
    let currentYear = now.getFullYear();
    let currentYearStr = String(currentYear);
    let currentYearPartialStr = currentYearStr.substr(0, 2);
    let currentYearPartial = Number(currentYearPartialStr);

    let fullBirthdateYearStr = currentYearPartialStr + birthdate.substr(0, 2);
    if (Number(fullBirthdateYearStr) > currentYear) {
      passportInfo.birthDateYear = String(currentYearPartial - 1) + birthdate.substr(0, 2);
    } else {
      passportInfo.birthDateYear = fullBirthdateYearStr;
    }

    passportInfo.dob =
      passportInfo.birthDateYear +
      '-' +
      passportInfo.birthDateMonth +
      '-' +
      passportInfo.birthDateDay;

    passportInfo.gender = track2.substring(20, 21);

    let expirationDate = track2.substring(21, 27);

    const fullExpYearStr = currentYearPartialStr + expirationDate.substr(0, 2);

    passportInfo.expirationDateYear = fullExpYearStr;

    passportInfo.expirationDateMonth = expirationDate.substr(2, 2);
    passportInfo.expirationDateDay = expirationDate.substr(4, 2);
  } catch (e) {
    // passport parsing is best-effort; incomplete data handled downstream
  }
  return passportInfo;
}

function specialCountryCodeMap(countryCode) {
  let newCode = specialCountryCodeMapping[countryCode];
  if (newCode != null) {
    return newCode;
  }
  return countryCode;
}

function validateLimitBag(passengers: Passenger[]) {
  return passengers?.find(
    (p) =>
      p.bagStack.filter((b) => b.status == StatusBag.INCLUDE).length >= 3 &&
      p.checkinStatus === true
  );
}

function removeAccents(str: string) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Fase de la ventana de documentacion — Fase explícita de la ventana de documentación de equipaje.
 *
 * `scheduleChange` sólo responde "dentro o fuera de ventana"
 */
function getDocumentationWindowPhase(
  flightSummaryParam: FlightSummary,
  _location?: string
): DocumentationWindowPhase {
  const { departureDateTime, isDomestic } = flightSummaryParam ?? ({} as FlightSummary);
  const departureDate = moment(departureDateTime);
  const openingDate = departureDate
    .clone()
    .subtract(DOCUMENTATION_WINDOW.OPENING_HOURS, 'hours')
    .toDate();
  const closingDate = departureDate
    .clone()
    .subtract(getClosingMinutes(isDomestic), 'minutes')
    .toDate();
  const now = new Date();

  if (closingDate <= now) {
    return DocumentationWindowPhase.CLOSED;
  }
  if (openingDate > now) {
    return DocumentationWindowPhase.BEFORE_OPEN;
  }
  return DocumentationWindowPhase.OPEN;
}

function scheduleChange(flightSummaryParam: FlightSummary, _location?: string): boolean {
  return (
    getDocumentationWindowPhase(flightSummaryParam, _location) !== DocumentationWindowPhase.OPEN
  );
}

function isNight(compareDate: string): boolean {
  const date = moment(compareDate);
  const initDate = moment('19:00', 'HH:mm');
  const finalDate = moment('06:00', 'HH:mm').add(1, 'day');

  return date.isBetween(initDate, finalDate);
}

function formatDateYYYMMDD(inputDate: string): string {
  return moment(inputDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
}
function toBase64(cadena: string) {
  return btoa(unescape(encodeURIComponent(cadena)));
}

function showBaggageButtonEnabledForMainLightOutsideWindow(
  fareFamily: string,
  passengerSelected: Passenger[] | null | undefined,
  disabledBaggageDrop: boolean
): boolean {
  if (!fareFamily || fareFamily !== FARES_FAMILY.MAIN_LIGHT) {
    return false;
  }
  if (!disabledBaggageDrop) {
    return false;
  }
  const passengers = passengerSelected ?? [];
  const hasPassengersWithoutCarryOn = passengers.some((p) => {
    if (p.paxType === PAX_TYPE.INFANT) {
      return false;
    }
    const stack = p.bagStackCarryon ?? [];
    const hasCarryOnPurchased = stack.some(
      (b) => b.status === StatusBag.PAID || b.status === StatusBag.INCLUDE
    );
    return !hasCarryOnPurchased;
  });
  return hasPassengersWithoutCarryOn;
}

function showBaggageButtonEnabledForBasicOutsideWindow(
  fareFamily: string,
  passengerSelected: Passenger[] | null | undefined,
  disabledBaggageDrop: boolean
): boolean {
  if (!fareFamily || !CARRY_ON_BASIC_FARE_FAMILIES.includes(fareFamily as FARES_FAMILY)) {
    return false;
  }
  if (!disabledBaggageDrop) {
    return false;
  }
  const passengers = passengerSelected ?? [];
  const hasPassengersWithout5kgAddOn = passengers.some((p) => {
    if (p.paxType === PAX_TYPE.INFANT) {
      return false;
    }
    const has5kgAddOn = p.bagStackCarryon?.some((bag) => bag.rficSubcode === '07X') ?? false;
    return !has5kgAddOn;
  });
  return hasPassengersWithout5kgAddOn;
}

function showBaggageButtonEnabledOutsideDocWindow(
  fareFamily: string,
  passengerSelected: Passenger[] | null | undefined,
  disabledBaggageDrop: boolean
): boolean {
  return (
    showBaggageButtonEnabledForMainLightOutsideWindow(
      fareFamily,
      passengerSelected,
      disabledBaggageDrop
    ) ||
    showBaggageButtonEnabledForBasicOutsideWindow(
      fareFamily,
      passengerSelected,
      disabledBaggageDrop
    )
  );
}

/**
 * CI-19584 — Statuses que indican que el pasajero "ya tiene" un Carry-On.
 * Incluye estados de compra (PAID), inclusión por tarifa (INCLUDE), redención
 * pendiente / aplicada (REDEEMED / REDEEMED_APPLIED) y documentado (DOCUMENT).
 * El check de `b.used === true` cubre el escenario en que el bag ya fue
 * "consumido" por bag-tag sin status DOCUMENT explícito.
 */
const ACQUIRED_CARRY_ON_STATUSES: StatusBag[] = [
  StatusBag.PAID,
  StatusBag.INCLUDE,
  StatusBag.REDEEMED,
  StatusBag.REDEEMED_APPLIED,
  StatusBag.DOCUMENT,
];

function allPassengersHaveCarryOnMainLight(
  passengerSelected: Passenger[] | null | undefined
): boolean {
  const passengers = passengerSelected ?? [];
  if (passengers.length === 0) {
    return false;
  }
  const allHaveIt = passengers.every((p) => {
    if (p.paxType === PAX_TYPE.INFANT) {
      return true;
    }
    const stack = p.bagStackCarryon ?? [];
    // CI-19584: considerar también REDEEMED/REDEEMED_APPLIED/DOCUMENT (y `used`)
    // para evitar que la modal "Agregar equipaje de mano" se abra cuando el pax
    // ya redimió Carry-On (típicamente con INBURSA) o ya lo documentó.
    return stack.some(
      (b) => ACQUIRED_CARRY_ON_STATUSES.includes(b.status as StatusBag) || b.used === true
    );
  });
  return allHaveIt;
}

export {
  formatDateYYYMMDD,
  parseDate,
  abbreviateString,
  shortenFfName,
  getUniqueId,
  validatePNR,
  parsePassportInfo,
  unsubscribe,
  validateLimitBag,
  removeAccents,
  scheduleChange,
  getDocumentationWindowPhase,
  isNight,
  showBaggageButtonEnabledForMainLightOutsideWindow,
  showBaggageButtonEnabledForBasicOutsideWindow,
  showBaggageButtonEnabledOutsideDocWindow,
  allPassengersHaveCarryOnMainLight,
};
